import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import TrendCard from "../components/TrendCard";
import Avatar from "../components/Avatar";
import ShareLineupCard from "../components/ShareLineupCard";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { getContest, getTrendIndexes, joinContest } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Sparkles, Crown, Shield, X, Info } from "lucide-react";

const CATS = ["all", "bollywood", "reality", "creator", "music", "athletes"];

export default function BuildLineup() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [contest, setContest] = useState(null);
  const [indexes, setIndexes] = useState([]);
  const [cat, setCat] = useState("all");
  const [picks, setPicks] = useState([]); // array of index_ids
  const [captain, setCaptain] = useState(null);
  const [vc, setVc] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [shareId, setShareId] = useState(null);

  useEffect(() => {
    Promise.all([getContest(id), getTrendIndexes()]).then(([c, i]) => {
      setContest(c);
      setIndexes(i);
    });
  }, [id]);

  const filtered = useMemo(
    () => (cat === "all" ? indexes : indexes.filter(x => x.category === cat)),
    [indexes, cat]
  );

  const pickedSet = new Set(picks);
  const picksDetail = picks
    .map((pid) => indexes.find((x) => x.index_id === pid))
    .filter(Boolean);
  // Projected total points: base form points × multiplier (C=2, VC=1.5, others=1)
  const projectedPoints = picksDetail.reduce((a, x) => {
    const base = Number(x.form_points ?? x.heat_score ?? 0);
    const mult = x.index_id === captain ? 2 : x.index_id === vc ? 1.5 : 1;
    return a + base * mult;
  }, 0);

  const togglePick = (idx) => {
    if (pickedSet.has(idx.index_id)) {
      const next = picks.filter((p) => p !== idx.index_id);
      setPicks(next);
      if (captain === idx.index_id) setCaptain(null);
      if (vc === idx.index_id) setVc(null);
    } else {
      if (picks.length >= 6) {
        toast("Lineup full — remove a pick to swap.");
        return;
      }
      setPicks([...picks, idx.index_id]);
    }
  };

  const submit = async () => {
    if (picks.length !== 6) return toast.error("Pick exactly 6 indexes.");
    if (!captain || !vc) return toast.error("Choose a Captain and Vice-Captain.");
    if (captain === vc) return toast.error("Captain and VC must differ.");
    setSubmitting(true);
    try {
      const res = await joinContest(id, { contest_id: id, picks, captain_id: captain, vice_captain_id: vc });
      confetti({ particleCount: 120, spread: 80, origin: { y: 0.6 }, colors: ["#B026FF", "#00F0FF", "#00FF66"] });
      toast.success("Lineup locked in! Share it to Instagram 📸");
      await refresh();
      if (res?.lineup_id) {
        setShareId(res.lineup_id);
      } else {
        nav(`/contests/${id}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit lineup.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pb-safe" data-testid="build-lineup-page">
      <Header title="Build Circle" />
      <div className="max-w-xl mx-auto px-4 pt-4 space-y-4">
        {contest && (
          <div className="glass rounded-2xl p-4">
            <p className="text-xs uppercase tracking-widest text-white/50 font-bold">{contest.mode === "standard" ? contest.category : contest.mode}</p>
            <h1 className="font-display text-2xl font-bold mt-1">{contest.name}</h1>
            <p className="text-sm text-white/60">Build your 6-star Influencer Circle. Crown 1. Own the culture.</p>
            <div className="mt-3 rounded-xl p-3 flex items-start gap-2" style={{ background: "#00F0FF10", border: "1px solid #00F0FF33" }}>
              <Info className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "#00F0FF" }} />
              <p className="text-xs text-white/75 leading-relaxed">
                <b>Quick rules:</b> Tap any 6 stars to add them. Mark 1 as <span style={{ color: "#B026FF" }}>Captain</span> (2× points) and 1 as <span style={{ color: "#00F0FF" }}>Vice-Captain</span> (1.5×). Entry: <b className="text-[#FFD600]">{(contest.entry_fee || 0).toLocaleString("en-IN")} credits</b> — no real money ever.
              </p>
            </div>
          </div>
        )}

        {/* Picks tray */}
        <div className="glass-strong rounded-2xl p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-[#00F0FF]" />
              <p className="font-display font-semibold">Your Influencer Circle ({picks.length}/6)</p>
            </div>
            <span className="text-xs font-mono-brand" style={{ color: projectedPoints > 0 ? "#FFD600" : "rgba(255,255,255,0.5)" }} data-testid="projected-points">
              ⚡ {projectedPoints.toFixed(0)} proj. pts
            </span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {Array.from({ length: 6 }).map((_, i) => {
              const idx = picksDetail[i];
              if (!idx) return (
                <div key={i} className="aspect-square rounded-xl border border-dashed border-white/15 grid place-items-center text-white/30 text-xs">
                  Slot {i + 1}
                </div>
              );
              const isC = captain === idx.index_id;
              const isV = vc === idx.index_id;
              return (
                <div key={idx.index_id} className={`relative rounded-xl overflow-hidden ring-1 ${isC ? "ring-[#B026FF]" : isV ? "ring-[#00F0FF]" : "ring-white/10"}`}>
                  <div className="w-full aspect-square">
                    <Avatar name={idx.name} category={idx.category} symbol={idx.symbol} size={140} rounded={0} className="!w-full !h-full !ring-0" />
                  </div>
                  <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(180deg,transparent 40%, rgba(0,0,0,0.85))" }} />
                  <button
                    onClick={() => togglePick(idx)}
                    className="absolute top-1 right-1 z-20 w-6 h-6 rounded-full bg-black/60 grid place-items-center"
                    aria-label="remove"
                    data-testid={`remove-pick-${idx.handle}`}
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                  <div className="absolute bottom-1 left-1 right-1 z-20">
                    <p className="text-[10px] font-bold truncate pointer-events-none">{idx.name}</p>
                    <div className="flex gap-1 mt-1">
                      <button
                        onClick={(e) => { e.stopPropagation(); setCaptain(captain === idx.index_id ? null : idx.index_id); }}
                        data-testid={`captain-${idx.handle}`}
                        className={`text-[9px] px-1.5 py-0.5 rounded ${isC ? "bg-[#B026FF] text-black font-bold" : "bg-white/10"}`}
                      >C 2x</button>
                      <button
                        onClick={(e) => { e.stopPropagation(); setVc(vc === idx.index_id ? null : idx.index_id); }}
                        data-testid={`vc-${idx.handle}`}
                        className={`text-[9px] px-1.5 py-0.5 rounded ${isV ? "bg-[#00F0FF] text-black font-bold" : "bg-white/10"}`}
                      >VC 1.5x</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div className="flex gap-2 mt-3 text-[11px] text-white/50">
            <span className="inline-flex items-center gap-1"><Crown className="w-3 h-3 text-[#B026FF]" /> Captain earns 2× points.</span>
            <span className="inline-flex items-center gap-1"><Shield className="w-3 h-3 text-[#00F0FF]" /> VC earns 1.5× points.</span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 overflow-x-auto py-1">
          {CATS.map((c) => (
            <button
              key={c}
              data-testid={`build-filter-${c}`}
              onClick={() => setCat(c)}
              className="chip whitespace-nowrap px-3 py-1.5 text-xs"
              style={cat === c
                ? { background: "linear-gradient(90deg,#B026FF,#00F0FF)", color: "#000", border: "1px solid transparent" }
                : { background: "rgba(255,255,255,0.05)", color: "#fff", border: "1px solid rgba(255,255,255,0.1)" }}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="space-y-3">
          {filtered.slice(0, 60).map((idx) => (
            <TrendCard
              key={idx.index_id}
              index={idx}
              selected={pickedSet.has(idx.index_id)}
              captain={captain === idx.index_id}
              vc={vc === idx.index_id}
              onClick={() => togglePick(idx)}
            />
          ))}
        </div>

        <button
          onClick={submit}
          disabled={submitting || picks.length !== 6 || !captain || !vc}
          data-testid="submit-lineup-btn"
          className="sticky bottom-24 w-full py-3.5 rounded-full font-bold text-black disabled:opacity-40"
          style={{ background: "linear-gradient(90deg,#00F0FF,#B026FF)", boxShadow: "0 0 22px rgba(176,38,255,0.35)" }}
        >
          {submitting ? "Locking in…" : contest?.entry_fee ? `Join · ${(contest.entry_fee || 0).toLocaleString("en-IN")} credits` : "Join Free"}
        </button>
        <div className="h-10" />
      </div>
      <BottomNav />
      <ShareLineupCard
        open={!!shareId}
        lineupId={shareId}
        onClose={() => { setShareId(null); nav(`/contests/${id}`); }}
      />
    </div>
  );
}
